/**
 * Copyright ©2023 Drivepoint
 */

import React, {createRef, forwardRef, useImperativeHandle} from "react";
import {useModal} from "mui-modal-provider";
import {closeSnackbar} from "notistack";
import {useStore} from "@bainbridge-growth/node-frontend";
import ACLDialog, {ACLDialogProps} from "@components/dazzler/DazzlerStudio/DazzlerStudioExplorer/ACLDialog/ACLDialog";
import DPShareModal, {type AccessEntry, type DPShareModalProps} from "@components/DPDialog/modals/DPShareModal/DPShareModal";
import useStateChange from "@hooks/useStateChange";
import {accessListToGrants, buildShareables, grantsToAccessList} from "@services/acl/manageAccess";
import WebAppServerClient from "@services/clients/WebAppServerClient";
import FavoritesService from "@services/route/FavoritesService";
import ServiceRegistry from "@services/ServiceRegistry";
import State from "@services/state/State";
import DazzlerDocumentsStore from "@services/store/DazzlerDocumentsStore";
import ReportsStore from "@services/store/ReportsStore";
import Toast from "@services/Toast";
import DrivepointUser from "@services/user/DrivepointUser";
import DPMenu, {StandardMenuInterface, StandardMenuItem} from "../DPMenu/DPMenu";

export type NavigationPageItemMenuProps = {
  inNav?: boolean;
  onPageEdit?: (page: any) => void;
  ref?: any;
};

export type NavigationPageItemMenuInterface = {
} & StandardMenuInterface;

const NavigationPageItemMenu = forwardRef<NavigationPageItemMenuInterface, NavigationPageItemMenuProps>((props: NavigationPageItemMenuProps, ref: any): any => {

  useImperativeHandle(ref, (): NavigationPageItemMenuInterface => ({
    open: (element: HTMLElement, data?: any): void => {
      if (props.inNav && !data?.favorite) { return; }
      menu.current?.open(element, data);
    },
    close: () => close()
  }));

  const menu = createRef<StandardMenuInterface>();

  const {showModal} = useModal();

  const [reports] = useStore<any[], ReportsStore>(ReportsStore);
  const [_, dazzlerDocumentsStore] = useStore<any[], DazzlerDocumentsStore>(DazzlerDocumentsStore);

  const company = useStateChange<any>("company");
  const flags = useStateChange<any>("flags");
  const user = useStateChange<DrivepointUser>("user");

  const actionsMenuItems: StandardMenuItem[] = [
    {id: "favorite", label: "${data?.favorite ? 'Remove Favorite' : 'Add Favorite'}"},
    {id: "edit", label: "Edit", visible: "${!!data?.custom}"},
    ...((flags?.["section.company.new_team.enabled"] && (user?.excelUser?.role === "admin" || user?.excelUser?.role === "superAdmin")) ? [{id: "manage_access", label: "Manage Access", visible: "${data?.report_type === 'dazzler' || data?.report_type === 'html-report'}"}] : []),
    {id: "access_rules", label: "Access Control", visible: "${data?.report_type === 'dazzler' && user?.excelUser?.role === 'superAdmin'}"}
  ];

  async function toggleFavorite(data: any): Promise<void> {
    const msg = data?.favorite ? `Removing ${data.name} from Favorites...` : `Adding ${data.name} to Favorites...`;
    const toastKey = Toast.progress(msg);
    let payload = {};
    if (data?.report_type === "dazzler") {
      payload = {type: "dazzler", id: data.id, name: data.name, company: company.id};
    }
    if (!user || !company) { return; }
    await WebAppServerClient.request(`/api/company/${company.id}/user/${user.user.email}/favorite/${data.id}/toggle`, "PATCH", payload);
    FavoritesService.clear();
    const sections = await ServiceRegistry.routeService.rehydrate();
    State.set("routes", {...State.get("routes"), sections});
    closeSnackbar(toastKey);
  }

  async function edit(page: any): Promise<void> {
    if (props.onPageEdit) { props.onPageEdit(page); }
  }

  function getAccessApi(reportType?: string) {
    switch (reportType) {
      case "dazzler":
        return {
          get: (companyId: string, id: string) => WebAppServerClient.getDazzlerAccess(companyId, id),
          update: (companyId: string, id: string, grants: any) => WebAppServerClient.updateDazzlerAccess(companyId, id, grants)
        };
      case "html-report":
        return {
          get: (companyId: string, id: string) => WebAppServerClient.getReportAccess(companyId, id),
          update: (companyId: string, id: string, grants: any) => WebAppServerClient.updateReportAccess(companyId, id, grants)
        };
      default:
        return undefined;
    }
  }

  async function handleManageAccess(data: any): Promise<void> {
    if (!company?.id || !data?.id) { return; }
    const accessApi = getAccessApi(data.report_type);
    if (!accessApi) { return; }
    const resourceName = data.name ?? "Report";
    let originalGrants: any = {};
    showModal<DPShareModalProps>(DPShareModal, {
      resourceName,
      load: async () => {
        try {
          const [shareableItems, access] = await Promise.all([
            buildShareables(company.id),
            accessApi.get(company.id, data.id)
          ]);
          originalGrants = access?.grants ?? {};
          return {shareableItems, initialAccessList: grantsToAccessList(originalGrants, shareableItems, user?.excelUser?.id)};
        } catch (error: any) {
          Toast.error(error instanceof Error ? error : new Error(`Failed to load access for ${resourceName}`));
          throw error;
        }
      },
      onSave: async (accessList: AccessEntry[]) => {
        try {
          await accessApi.update(company.id, data.id, accessListToGrants(accessList, originalGrants));
          Toast.success(`Access to ${resourceName} updated successfully`);
        } catch (error: any) {
          Toast.error(error instanceof Error ? error : new Error(`Failed to update access to ${resourceName}`));
        }
      }
    });
  }

  async function onActionsMenuItemClick(item: StandardMenuItem, data?: any): Promise<void> {
    if (!data) { return; }
    switch (item.id) {
      case "favorite":
        toggleFavorite(data);
        break;
      case "edit":
        edit(data);
        break;
      case "manage_access":
        await handleManageAccess(data);
        break;
      case "access_rules":
        const item = reports.find(report => report.id === data.id);
        if (!item) { return; }
        showModal<ACLDialogProps>(ACLDialog, {item, onChange: (rules: any[]) => {
          item.acl = rules;
          dazzlerDocumentsStore.update(item);
        }});
        break;
    }
  }

  return <DPMenu items={actionsMenuItems} onClick={onActionsMenuItemClick} ref={menu} />;

});

export default NavigationPageItemMenu;
